import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { type ApiError, api, setAccessToken } from '~/lib/api';
import { hasAuthHint, setAuthHint } from '~/lib/auth-hint';
import { connectRealtime, disconnectRealtime } from '~/lib/realtime';
import { useNotificationsStore } from '~/stores/notifications';
import { useOrderBadgeStore } from '~/stores/order-badge';
import { useQuestionBadgeStore } from '~/stores/question-badge';

export interface AuthUser {
  id: string;
  email: string;
  phone?: string | null;
  name: string;
  role: 'admin' | 'store_owner';
  active: boolean;
  twoFactorEnabled: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const loaded = ref(false);
  const hydrating = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.role === 'admin');

  async function hydrate(): Promise<void> {
    if (hydrating.value) return;
    // Skip the network call when no prior login signal exists — the public
    // shell (Home, Login, Forgot) is a guest by default. Keeps the cold load
    // quiet and avoids spurious /auth/me calls on Lighthouse runs.
    if (!hasAuthHint()) {
      user.value = null;
      loaded.value = true;
      return;
    }
    hydrating.value = true;
    try {
      const res = await api.get<{ user: AuthUser }>('/auth/me');
      user.value = res.data.user;
      attachRealtime();
    } catch (err: unknown) {
      const status = (err as ApiError | undefined)?.status ?? 0;
      // Only a genuine auth rejection (401/403) means the session is really
      // over. Transient failures — 429 rate-limit, network blips, 5xx — must
      // NOT log the user out: keep the auth hint so the session recovers on the
      // next attempt instead of bouncing to /login.
      if (status === 401 || status === 403) {
        user.value = null;
        setAuthHint(false);
      }
    } finally {
      hydrating.value = false;
      loaded.value = true;
    }
  }

  function attachRealtime(): void {
    connectRealtime();
    const notifications = useNotificationsStore();
    notifications.attach();
    // Fire-and-forget — failures here surface elsewhere.
    void notifications.refreshCount();
    // Order/question badges listen for `order.new` / `question.new` events; the
    // counts themselves are fetched by AppLayout once the active store is known.
    useOrderBadgeStore().attach();
    useQuestionBadgeStore().attach();
  }

  /**
   * Logs in and stores tokens. Throws an ApiError on failure. On OTP_REQUIRED
   * the caller should re-prompt for the code and call this again with `otp`.
   */
  async function login(input: {
    identifier: string;
    password: string;
    otp?: string;
  }): Promise<AuthUser> {
    const res = await api.post<{
      user: AuthUser;
      accessToken: string;
    }>('/auth/login', input);
    setAccessToken(res.data.accessToken);
    user.value = res.data.user;
    setAuthHint(true);
    attachRealtime();
    return res.data.user;
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore; we're clearing local state regardless
    }
    setAccessToken(null);
    user.value = null;
    setAuthHint(false);
    disconnectRealtime();
    useNotificationsStore().reset();
    useOrderBadgeStore().reset();
    useQuestionBadgeStore().reset();
  }

  return {
    user,
    loaded,
    isAuthenticated,
    isAdmin,
    hydrate,
    login,
    logout,
  };
});

export function isOtpRequired(err: ApiError): boolean {
  if (err.status !== 401) return false;
  const reason = (err.details as { reason?: string } | undefined)?.reason;
  return reason === 'OTP_REQUIRED';
}
